/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { inject, Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CriteriaDataType, CriteriaOperator } from '../../../app/modules/models/criteria/criteria.enums';
import { SearchCriteriaDto, SearchCriteriaTypeEnum } from '../../../app/modules/models/criteria/search-criteria.interface';
import { ReclassificationService } from '../../../app/modules/services/reclassification.service';

@Injectable({
  providedIn: 'root',
})
export class ReclassificationValidatorService {
  private reclassificationService = inject(ReclassificationService);

  private auTitleSubject = new BehaviorSubject<string>('');
  debounceTime = 400;

  emitArchiveUnitTitle(auTitle: string) {
    this.auTitleSubject.next(auTitle);
  }

  getArchiveUnitTitle(): Observable<string> {
    return this.auTitleSubject.asObservable();
  }

  alreadyExistParents(codeToIgnore?: string, archiveUnitAllunitup?: string[]): AsyncValidatorFn {
    return (control: AbstractControl) => {
      return timer(this.debounceTime).pipe(
        switchMap(() =>
          control.value !== codeToIgnore ? of(this.isAlreadyExistingParentValue(control.value, archiveUnitAllunitup)) : of(false),
        ),
        take(1),
        map((exists: boolean) => (exists ? { alreadyExistParents: true } : null)),
      );
    };
  }

  isAlreadyExistingParentValue(parentId: string, archiveUnitAllunitup: string[]): boolean {
    return true ? archiveUnitAllunitup.filter((p) => p === parentId).length > 0 : false;
  }

  existArchiveUnit(criteriaDto: SearchCriteriaDto): AsyncValidatorFn {
    return this.unitExists('targetGuid', criteriaDto);
  }

  private unitExists(existTag: string, criteriaDto: SearchCriteriaDto) {
    return (control: AbstractControl) => {
      const auditExists: any = {};
      auditExists[existTag] = true;
      const searchCriteria: SearchCriteriaDto = {
        pageNumber: criteriaDto.pageNumber,
        size: criteriaDto.size,
        criteriaList: [
          {
            criteria: 'GUID',
            operator: CriteriaOperator.EQ,
            category: SearchCriteriaTypeEnum[SearchCriteriaTypeEnum.FIELDS],
            dataType: CriteriaDataType.STRING,
            values: [{ value: control.value, id: control.value }],
          },
        ],
      };
      const result = timer(this.debounceTime).pipe(
        switchMap(() =>
          control.value !== null
            ? this.reclassificationService
                .searchArchiveUnitsByCriteria(searchCriteria)
                .toPromise()
                .then((data) => {
                  if (data.totalResults === 1) {
                    this.emitArchiveUnitTitle(ReclassificationService.fetchAuTitle(data.results[0]));
                    return false;
                  } else {
                    this.emitArchiveUnitTitle(null);
                    return true;
                  }
                })
            : of(false),
        ),
        take(1),
        map((exists: boolean) => (exists ? auditExists : null)),
      );

      return result;
    };
  }
}
